import { Plus, Search, X } from 'lucide-react';
import * as React from 'react';
import { Translate } from 'react-localize-redux';
import styled from 'styled-components';
import { ITag } from '../../../typings/proto/protobufs';
import { checkClickOut } from './Dropdown';
import Dropdown2 from './Dropdown2';
import { DropdownTagSearch } from './DropdownTagsCloud';
import PageLoader from '../pageLoader/PageLoader';
import { DefaultText } from '../../styles/global/css/GlobalText';
import { BorderColor, GreySidely2, GreySidely3, LightGreySidely, PrimaryColor } from '../../styles/global/css/Utils';
import { translateToString } from '../../styles/global/translate';
import { TableRowStatus, TableRowStatusBackground } from '../../containers_v2/orders/templateOrders/style/Style';

const Wrapper = styled.div<{ paddingLeft?: string }>`
	width: 100%;
	min-width: 0;
	padding-left: ${p => p.paddingLeft ?? '0'};
	box-sizing: border-box;
`;

const TagRow = styled.div<{ horizontal?: boolean }>`
	display: flex;
	flex-wrap: ${p => p.horizontal ? 'nowrap' : 'wrap'};
	align-items: center;
	justify-content: flex-start;
	gap: 6px;
	width: 100%;
	${p => p.horizontal ? `
	overflow-x: auto;
	scrollbar-width: none;
	&::-webkit-scrollbar {
		display: none;
	}
	& > * {
		flex-shrink: 0;
		white-space: nowrap;
	}
	` : ''}
`;

const NoneText = styled.span`
	${DefaultText}
`;

const PILL_LINE_HEIGHT = '16px';

const TagPill = styled(TableRowStatus)`
	display: inline-flex;
	background-color: transparent;
	align-items: center;
	gap: 4px;
	max-width: 100%;
	line-height: ${PILL_LINE_HEIGHT};
	white-space: normal;
	overflow: visible;
	word-break: break-word;
`;

const OptionPill = styled(TableRowStatus)`
	white-space: normal;
	overflow: visible;
	word-break: break-word;
`;

const RemoveIcon = styled.span`
	display: inline-flex;
	flex-shrink: 0;
	cursor: pointer;
	&:hover {
		color: ${PrimaryColor};
	}
`;

const AddTag = styled.div<{ hoverOnly?: boolean }>`
	${DefaultText}
	display: inline-flex;
	flex-shrink: 0;
	${p => p.hoverOnly ? `
	visibility: hidden;
	${Wrapper}:hover & {
		visibility: visible;
	}
	` : ''}
	align-items: center;
	gap: 4px;
	padding: 1px 7px;
	border: 1px dashed ${GreySidely3};
	border-radius: 8px;
	color: ${GreySidely3};
	font-size: 11px;
	font-weight: 500;
	line-height: ${PILL_LINE_HEIGHT};
	min-height: calc(${PILL_LINE_HEIGHT} + 4px);
	box-sizing: border-box;
	white-space: nowrap;
	cursor: pointer;
	transition: color 0.2s, border-color 0.2s, background-color 0.2s;
	&:hover {
		color: ${PrimaryColor};
		border-color: ${PrimaryColor};
		background-color: color-mix(in oklab, ${PrimaryColor} 8%, transparent);
	}
`;

const Popup = styled.div`
	display: flex;
	flex-direction: column;
	width: 240px;
`;

const SearchRow = styled.div`
	display: flex;
	align-items: center;
	gap: 6px;
	padding: 2px 6px 8px;
	margin-bottom: 4px;
	border-bottom: 1px solid ${BorderColor};
	color: ${GreySidely3};
`;

const OptionList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 2px;
	max-height: 220px;
	overflow-y: auto;
`;

const Option = styled(TableRowStatusBackground)`
	align-items: center;
	padding: 4px 6px;
	border-radius: 5px;
	&:hover {
		background-color: ${LightGreySidely};
	}
`;

const NoOption = styled.div`
	${DefaultText}
	padding: 8px;
	text-align: center;
	color: ${GreySidely3};
`;

export default function TagsPicker(props: {
	tags: ITag[]
	selected: ITag[]
	loading?: boolean
	readOnly?: boolean
	horizontal?: boolean
	addOnHover?: boolean
	noRemove?: boolean
	paddingLeft?: string
	onAdd?: (tag: ITag) => void
	onDelete?: (id: number) => void
}): JSX.Element {
	const [search, setSearch] = React.useState('');
	const [isOpen, setIsOpen] = React.useState(false);
	const [optionUp, setOptionUp] = React.useState(false);
	const wrapperRef = React.useRef<HTMLDivElement>();

	checkClickOut(wrapperRef, setIsOpen);

	React.useEffect(() => {
		if (!isOpen) setSearch('');
	}, [isOpen]);

	const pills = props.selected.map(t => <TagPill key={t.id} backgroundColor={t.color ?? undefined}>
		{t.name}
		{!props.readOnly && !props.noRemove && <RemoveIcon onClick={() => props.onDelete?.(t.id!)}><X size={11} color='currentColor' /></RemoveIcon>}
	</TagPill>);

	if (props.readOnly) return <Wrapper paddingLeft={props.paddingLeft}><TagRow horizontal={props.horizontal}>{pills}</TagRow></Wrapper>;

	const options = props.tags
		.filter(t => !props.selected.some(s => s.id === t.id))
		.filter(t => !search || t.name?.toLocaleLowerCase().includes(search.toLocaleLowerCase()))
		.sort((a, b) => {
			if (a.color && b.color && a.color !== b.color) return a.color.localeCompare(b.color);
			return (a.name ?? '').localeCompare(b.name ?? '');
		});

	return <Wrapper paddingLeft={props.paddingLeft} innerRef={(node: HTMLDivElement | null) => wrapperRef.current = node ?? undefined}><Dropdown2 isOpenState={[isOpen, setIsOpen]} bottom={optionUp} button={({ setIsOpen }) => <TagRow horizontal={props.horizontal}>
		{pills}
		{props.addOnHover && props.selected.length === 0 && <NoneText>-</NoneText>}
		<AddTag hoverOnly={props.addOnHover} onClick={e => {
			setOptionUp(e.clientY >= window.innerHeight / 2);
			setIsOpen(open => !open);
		}}>
			<Plus size={11} color='currentColor' />
			{!props.addOnHover && props.selected.length === 0 && <Translate id='add_tags' />}
		</AddTag>
	</TagRow>}>
		<Popup>
			<SearchRow>
				{props.loading ? <PageLoader size={14} color={GreySidely2} /> : <Search size={14} color='currentColor' />}
				<DropdownTagSearch autoFocus placeholder={translateToString('search').trim()} value={search} onChange={e => setSearch(e.target.value)} />
			</SearchRow>
			<OptionList>
				{options.length === 0 && <NoOption><Translate id='no_option' /></NoOption>}
				{options.map(t => <Option key={t.id} onClick={() => props.onAdd?.(t)}>
					<OptionPill noBackground backgroundColor={t.color ?? undefined}>{t.name}</OptionPill>
				</Option>)}
			</OptionList>
		</Popup>
	</Dropdown2></Wrapper>;
}
